import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, X, Receipt, DollarSign, Calendar, Building, FileText, Tag } from 'lucide-react';
import { ExpenseService } from '../services/ExpenseService';
import { ExpenseCategoryService, ExpensePolicyService, ExpenseCategory, ExpensePolicy } from '../services/CategoryPolicyService';
import { useAuth } from '../contexts/AuthContext';
import ModernNavigation from './ModernNavigation';
import { ModernCard } from './ModernCards';

interface CategoryInReceipt {
  id: number;
  name: string;
  description?: string;
}

interface PolicyInReceipt {
  id: number;
  name: string;
  description?: string;
}

interface OCRData {
  extracted_amount: string | number | null | undefined;
  extracted_date: string | null | undefined;
  extracted_vendor: string | null | undefined;
  extracted_invoice_id: string | null | undefined;
  category_id?: number | null | undefined;
  policy_id?: number | null | undefined;
  category?: CategoryInReceipt | null | undefined;
  policy?: PolicyInReceipt | null | undefined;
}

interface ExpenseFormWithOCRProps {
  receiptId: number;
  ocrData: OCRData;
  onBack: () => void;
}

const ExpenseFormWithOCR: React.FC<ExpenseFormWithOCRProps> = ({
  receiptId,
  ocrData,
  onBack
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [categories, setCategories] = useState<ExpenseCategory[]>([]);
  const [policies, setPolicies] = useState<ExpensePolicy[]>([]);
  const [formData, setFormData] = useState({
    title: ocrData.extracted_vendor || '',
    amount: ocrData.extracted_amount ? Number(ocrData.extracted_amount).toFixed(2) : '',
    currency: 'USD',
    time: ocrData.extracted_date ? new Date(ocrData.extracted_date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
    vendor: ocrData.extracted_vendor || '',
    location: '',
    description: `Invoice ID: ${ocrData.extracted_invoice_id || 'N/A'}`,
    category_id: ocrData.category_id ? String(ocrData.category_id) : '',
    policy_id: ocrData.policy_id ? String(ocrData.policy_id) : ''
  });

  useEffect(() => {
    loadCategories();
    
    // If category_id is provided from OCR, load policies for that category
    if (ocrData.category_id) {
      loadPoliciesForCategory(String(ocrData.category_id));
    }
  }, []);

  const loadCategories = async () => {
    try {
      const categoriesData = await ExpenseCategoryService.getCategories();
      setCategories(categoriesData);
    } catch (err) {
      console.error('Error loading categories:', err);
      setError('Failed to load categories');
    }
  };

  const loadPoliciesForCategory = async (categoryId: string) => {
    try {
      const policiesData = await ExpensePolicyService.getPoliciesByCategory(parseInt(categoryId), user?.org_id || 1);
      setPolicies(policiesData);
    } catch (err) {
      console.error('Error loading policies for category:', err);
      setError('Failed to load policies for selected category');
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    
    if (name === 'category_id') {
      setFormData(prev => ({
        ...prev,
        [name]: value,
        policy_id: '' // Reset policy when category changes
      }));
      
      if (value) {
        loadPoliciesForCategory(value);
      } else {
        setPolicies([]);
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    // Validate required fields for submission
    if (!formData.category_id) {
      setError('Please select a category before submitting');
      setIsSubmitting(false);
      return;
    }

    if (!formData.policy_id) {
      setError('Please select a policy before submitting');
      setIsSubmitting(false);
      return;
    }

    try {
      const expenseData = {
        ...formData,
        amount: parseFloat(formData.amount),
        time: new Date(formData.time).toISOString(),
        receipt_id: receiptId,
        source: 'fiat' as const,
        user_id: user?.id || 0,
        org_id: user?.org_id || 1,
        category_id: parseInt(formData.category_id),
        policy_id: parseInt(formData.policy_id)
      };

      const response = await ExpenseService.createExpense(expenseData);
      
      if (response) {
        // Automatically submit the expense for approval
        try {
          await ExpenseService.submitExpense(response.id);
          navigate(`/expenses/${response.id}?submitted=true`);
        } catch (submitError: any) {
          console.error('Error submitting expense:', submitError);
          // Still navigate to the expense page even if submission fails
          navigate(`/expenses/${response.id}`);
        }
      }
    } catch (err: any) {
      console.error('Error creating expense:', err);
      setError(err.response?.data?.detail || 'Failed to create expense');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <ModernNavigation />
      
      {/* Main Content */}
      <main className="lg:pl-7">
        <div className="max-w-4xl mx-auto px-6 py-8">
          {/* Page Header */}
          <div className="bg-white border-b border-gray-200 -mx-6 px-6 py-8 mb-8">
            <div className="flex items-center justify-between">
              <div className="flex-1 text-center">
                <h1 className="text-3xl font-bold text-gray-900 font-display">
                  Create Expense
                </h1>
                <p className="mt-2 text-lg text-gray-600 font-body">
                  Review and edit the information extracted from your receipt
                </p>
              </div>
              <div className="flex items-center space-x-3">
                <button
                  onClick={onBack}
                  className="btn-ghost flex items-center space-x-2"
                >
                  <ArrowLeft className="h-4 w-4" />
                  <span>Back</span>
                </button>
              </div>
            </div>
          </div>
          
          {/* Page Content */}
          <div>
            {error && (
              <div className="mb-6 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg">
                {error}
              </div>
            )}

            {/* OCR Data Summary */}
            <ModernCard className="p-8 mb-8">
              <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center justify-center">
                <Receipt className="h-6 w-6 mr-3 text-green-600" />
                Information from Receipt
              </h3>
              <div className="max-w-2xl mx-auto">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="flex items-center space-x-4 p-4 bg-green-50 rounded-lg">
                    <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                      <DollarSign className="h-6 w-6 text-green-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-700">Amount</p>
                      <p className="text-lg font-semibold text-gray-900">
                        {ocrData.extracted_amount ? `$${Number(ocrData.extracted_amount).toFixed(2)}` : 'Not detected'}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-4 p-4 bg-green-50 rounded-lg">
                    <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                      <Building className="h-6 w-6 text-green-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-700">Vendor</p>
                      <p className="text-lg font-semibold text-gray-900">
                        {ocrData.extracted_vendor || 'Not detected'}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-4 p-4 bg-green-50 rounded-lg">
                    <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                      <Calendar className="h-6 w-6 text-green-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-700">Date</p>
                      <p className="text-lg font-semibold text-gray-900">
                        {ocrData.extracted_date ? new Date(ocrData.extracted_date).toLocaleDateString() : 'Not detected'}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-4 p-4 bg-green-50 rounded-lg">
                    <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                      <FileText className="h-6 w-6 text-green-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-700">Invoice ID</p>
                      <p className="text-lg font-semibold text-gray-900 break-all">
                        {ocrData.extracted_invoice_id || 'Not detected'}
                      </p>
                    </div>
                  </div>
                  
                  {(ocrData.category || ocrData.policy) && (
                    <div className="col-span-2 mt-4 p-4 bg-blue-50 border-2 border-blue-200 rounded-lg">
                      <div className="flex items-center mb-3">
                        <Tag className="h-5 w-5 text-blue-600 mr-2" />
                        <p className="text-sm font-semibold text-blue-900">AI Recommendations</p>
                      </div>
                      <div className="space-y-3 ml-7">
                        {ocrData.category && (
                          <div>
                            <p className="text-sm text-blue-800">
                              <span className="font-medium">Category:</span> <span className="font-semibold">{ocrData.category.name}</span>
                            </p>
                            {ocrData.category.description && (
                              <p className="text-xs text-blue-600 mt-1 ml-4">{ocrData.category.description}</p>
                            )}
                          </div>
                        )}
                        {ocrData.policy && (
                          <div>
                            <p className="text-sm text-blue-800">
                              <span className="font-medium">Policy:</span> <span className="font-semibold">{ocrData.policy.name}</span>
                            </p>
                            {ocrData.policy.description && (
                              <p className="text-xs text-blue-600 mt-1 ml-4">{ocrData.policy.description}</p>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </ModernCard>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-8">
              <ModernCard className="p-8">
                <div className="max-w-2xl mx-auto">
                  <h3 className="text-xl font-semibold text-gray-900 mb-8 flex items-center justify-center">
                    <Tag className="h-6 w-6 mr-3 text-green-600" />
                    Expense Details
                  </h3>
                  
                  <div className="space-y-8">
                    {/* Title - Full Width */}
                    <div className="w-full">
                      <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-3">
                        Expense Title *
                      </label>
                      <input
                        type="text"
                        id="title"
                        name="title"
                        value={formData.title}
                        onChange={handleInputChange}
                        required
                        className="input-modern w-full h-12"
                        placeholder="Enter expense title"
                      />
                    </div>

                    {/* Amount and Currency - Side by Side */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label htmlFor="amount" className="block text-sm font-medium text-gray-700 mb-3">
                          Amount *
                        </label>
                        <div className="relative">
                          <span className="absolute left-4 top-4 text-gray-500 text-lg">$</span>
                          <input
                            type="number"
                            id="amount"
                            name="amount"
                            value={formData.amount}
                            onChange={handleInputChange}
                            required
                            min="0"
                            step="0.01"
                            className="input-modern pl-10 w-full h-12"
                            placeholder="0.00"
                          />
                        </div>
                      </div>

                      <div>
                        <label htmlFor="currency" className="block text-sm font-medium text-gray-700 mb-3">
                          Currency
                        </label>
                        <select
                          id="currency"
                          name="currency"
                          value={formData.currency}
                          onChange={handleInputChange}
                          className="input-modern w-full h-12"
                        >
                          <option value="USD">USD</option>
                          <option value="EUR">EUR</option>
                          <option value="GBP">GBP</option>
                          <option value="CAD">CAD</option>
                        </select>
                      </div>
                    </div>

                    {/* Date and Vendor - Side by Side */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label htmlFor="time" className="block text-sm font-medium text-gray-700 mb-3">
                          Date *
                        </label>
                        <input
                          type="date"
                          id="time"
                          name="time"
                          value={formData.time}
                          onChange={handleInputChange}
                          required
                          className="input-modern w-full h-12"
                        />
                      </div>

                      <div>
                        <label htmlFor="vendor" className="block text-sm font-medium text-gray-700 mb-3">
                          Vendor
                        </label>
                        <input
                          type="text"
                          id="vendor"
                          name="vendor"
                          value={formData.vendor}
                          onChange={handleInputChange}
                          className="input-modern w-full h-12"
                          placeholder="Enter vendor name"
                        />
                      </div>
                    </div>

                    {/* Location and Category - Side by Side */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label htmlFor="location" className="block text-sm font-medium text-gray-700 mb-3">
                          Location
                        </label>
                        <input
                          type="text"
                          id="location"
                          name="location"
                          value={formData.location}
                          onChange={handleInputChange}
                          className="input-modern w-full h-12"
                          placeholder="Enter location"
                        />
                      </div>

                      <div>
                        <label htmlFor="category_id" className="block text-sm font-medium text-gray-700 mb-3 flex items-center">
                          Category *
                          {ocrData.category_id && (
                            <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                              AI Recommended
                            </span>
                          )}
                        </label>
                        <select
                          id="category_id"
                          name="category_id"
                          value={formData.category_id}
                          onChange={handleInputChange}
                          required
                          className={`input-modern w-full h-12 ${ocrData.category_id ? 'border-green-300 bg-green-50' : ''}`}
                        >
                          <option value="">Select a category</option>
                          {categories.map(category => (
                            <option key={category.id} value={category.id}>
                              {category.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* Policy - Full Width */}
                    <div className="w-full">
                      <label htmlFor="policy_id" className="block text-sm font-medium text-gray-700 mb-3 flex items-center">
                        Policy *
                        {ocrData.policy_id && (
                          <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                            AI Recommended
                          </span>
                        )}
                      </label>
                      <select
                        id="policy_id"
                        name="policy_id"
                        value={formData.policy_id}
                        onChange={handleInputChange}
                        required
                        className={`input-modern w-full h-12 ${ocrData.policy_id ? 'border-green-300 bg-green-50' : ''}`}
                        disabled={!formData.category_id}
                      >
                        <option value="">Select a policy</option>
                        {policies.map(policy => (
                          <option key={policy.id} value={policy.id}>
                            {policy.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Description - Full Width */}
                    <div className="w-full">
                      <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-3">
                        Description
                      </label>
                      <textarea
                        id="description"
                        name="description"
                        value={formData.description}
                        onChange={handleInputChange}
                        rows={4}
                        className="input-modern w-full resize-none"
                        placeholder="Enter expense description"
                      />
                    </div>
                  </div>
                </div>
              </ModernCard>

              {/* Submit Buttons */}
              <div className="max-w-2xl mx-auto flex justify-center space-x-4">
                <button
                  type="button"
                  onClick={onBack}
                  className="btn-secondary px-8 py-3 text-base font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-primary px-8 py-3 text-base font-medium flex items-center space-x-2"
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      <span>Creating...</span>
                    </>
                  ) : (
                    <>
                      <Save className="h-5 w-5" />
                      <span>Create & Submit Expense</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ExpenseFormWithOCR;